import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { routeTree } from './routeTree.gen'
import './styles.css'
import {
  initializeTokenStorage,
  saveAccessToken,
  hasSessionHint,
} from '@/lib/token-storage'
import { isSignedOutInUrl } from '@/lib/handle-signed-out-query'
import { API_BASE_URL } from '@/config/api'

if (typeof window !== 'undefined') {
  type ClarityFn = ((...args: unknown[]) => void) & { q: unknown[] }
  const win = window as Window & { clarity?: ClarityFn }
  if (!win.clarity) {
    const q: unknown[] = []
    const fn = ((...args: unknown[]) => {
      q.push(args)
    }) as ClarityFn
    fn.q = q
    win.clarity = fn
  }
  const t = document.createElement('script')
  t.async = true
  t.src = 'https://www.clarity.ms/tag/py77uz98wq'
  const first = document.getElementsByTagName('script')[0]
  first?.parentNode?.insertBefore(t, first)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
})

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
})

async function bootstrapLoginSessionFromCookie(): Promise<void> {
  try {
    await initializeTokenStorage()
    // Evita POST /refresh-token sem cookie no cold start (gera 401 esperado e
    // pode disparar limpeza de sessão no api-client em outros fluxos).
    if (!hasSessionHint()) {
      return
    }
    const base = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : ''
    const response = await fetch(`${base}/api/v2/auth/refresh-token`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    if (!response.ok) return
    const data = (await response.json()) as { accessToken?: string }
    if (data?.accessToken) {
      saveAccessToken(data.accessToken)
    }
  } catch {
    // sem sessão válida: segue fluxo normal de login
  }
}

if (!isSignedOutInUrl()) {
  await bootstrapLoginSessionFromCookie()
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
}
